<template>
  <v-container>
    <h1>Bulk Message</h1>
    <v-row class="justify-end mr-2 mb-5">
      <v-btn to="/chats/bulk-messages" class="text-right" color="primary">View bulk messages</v-btn>
    </v-row>
    <user-filter ref="filter" />
    <v-card class="mt-5">
      <validation-observer ref="observer">
        <form>
          <v-row class="mx-3">
            <v-col cols="12">
              <v-container v-if="bulkMessage">
                <v-alert type="info" v-if="bulkMessage.scheduledAt && !bulkMessage.sentAt"
                  >Scheduled to send on {{ niceDate(bulkMessage.scheduledAt) }}</v-alert
                >
                <v-alert type="success" v-if="bulkMessage.sentAt"
                  >Message Sent on: {{ niceDate(bulkMessage.sentAt) }}</v-alert
                >
              </v-container>
              <v-card-title class="ml-n4">Message Details</v-card-title>
              <validation-provider v-slot="{ errors }" name="Title" rules="required|max:200">
                <v-text-field
                  v-model="formData.title"
                  label="Title of bulk message"
                  :error-messages="errors"
                  :disabled="bulkMessage && !!bulkMessage.sentAt"
                />
              </validation-provider>
              <validation-provider v-slot="{ errors }" name="Message" rules="required">
                <vue-editor
                  v-model="formData.message"
                  label="Message"
                  :editor-toolbar="toolbar"
                  :error-messages="errors"
                  :disabled="bulkMessage && !!bulkMessage.sentAt"
                />
              </validation-provider>
              <p>Mail merge variables:</p>
              <ul>
                <li>#firstname#</li>
                <!--<li>#lastWorkoutInstructorFirstName# (not recommended to use this unless last workout filter is set)</li>
                <li>#lastWorkoutInstructorLastName# (not recommended to use this unless last workout filter is set)</li>-->
                <li>#currentProgramTitle# (not recommended to use this unless current workout block filter is set)</li>
                <li>#lastWorkoutType#</li>
              </ul>
            </v-col>
          </v-row>
          <v-row class="mx-3">
            <v-col cols="4">
              <validation-provider v-slot="{ errors }" name="Sender" rules="required">
                <v-select
                  v-model="formData.sender"
                  label="Sender"
                  :error-messages="errors"
                  :disabled="bulkMessage && !!bulkMessage.sentAt"
                  :items="coaches"
                />
              </validation-provider>
            </v-col>
          </v-row>
          <v-row class="mx-3">
            <v-col cols="4">
              <v-file-input
                v-model="formData.attachment"
                label="Attachment"
                show-size
                :disabled="bulkMessage && !!bulkMessage.sentAt"
              />
            </v-col>
            <v-col cols="4">
              <v-select
                v-model="formData.attachmentType"
                label="Attachment Type (check carefully)"
                :items="options.attachmentTypes"
                :disabled="bulkMessage && !!bulkMessage.sentAt"
              />
            </v-col>
          </v-row>
          <v-row class="mx-3">
            <v-col cols="4">
              <v-checkbox v-model="formData.sendNotification" label="Send Email Notification" />
            </v-col>
          </v-row>
          <v-row class="mx-3">
            <v-col cols="4">
              <v-btn v-if="!bulkMessage" :disabled="loading.creatingBulkMessage" @click="createBulkMessage">
                Create Bulk Message
              </v-btn>
              <v-btn
                v-else
                :disabled="loading.updatingBulkMessage || bulkMessage.sentAt !== null"
                @click="updateBulkMessage"
              >
                Update Bulk Message
              </v-btn>
            </v-col>
            <v-col v-if="bulkMessage && !bulkMessage.sentAt" cols="4">
              <v-btn @click="scheduleMessage">Schedule Message</v-btn>
            </v-col>
          </v-row>
        </form>
      </validation-observer>
    </v-card>
    <v-card v-if="bulkMessage" class="mt-5">
      <v-alert type="info">Selected: {{ selected.length }}</v-alert>
      <v-row>
        <v-col cols="12" class="text-right">
          <vue-json-to-csv :json-data="CSVData" :csv-title="bulkMessage.title">
            <v-btn class="mr-5">
              Download CSV
            </v-btn>
          </vue-json-to-csv>
        </v-col>
      </v-row>
      <v-data-table
        v-model="selected"
        :headers="table.headers"
        :items="recipientList"
        :items-per-page="-1"
        show-select
        item-key="id"
        @item-selected="handleRecipientChange"
        @toggle-select-all="handleRecipientChange"
      >
        <template v-slot:item.data-table-select="{ item, isSelected, select }">
          <v-simple-checkbox
            :value="isSelected"
            :readonly="item.disabled"
            :disabled="item.disabled"
            @input="select($event)"
            :ripple="false"
          ></v-simple-checkbox>
        </template>
      </v-data-table>
    </v-card>
    <v-dialog v-model="scheduleDialog" width="600">
      <v-card>
        <v-card-title>
          Schedule Bulk Message Send
        </v-card-title>
        <v-row class="mx-3">
          <v-col>
            <date-time-picker @changed="scheduleMessageDateTime = $event" />
          </v-col>
        </v-row>
        <v-row class="mx-3">
          <v-col>
            <v-btn @click="sendMessage" :disabled="scheduleMessageDateTime === null">Schedule Message</v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import {
  BULK_MESSAGE_QUERY,
  CREATE_BULK_MESSAGE_MUTATION,
  UPDATE_BULK_MESSAGE_MUTATION,
} from "@/constants/graphql/chat/chat";
import { VueEditor } from "vue2-editor";
import DateTimePicker from "@/components/shared/DateTimePicker";
import { fancy_date_from_iso } from "@/lib/fancy_date";
import VueJsonToCsv from "vue-json-to-csv";
import UserFilter from "@/components/shared/user/UserFilter";
import { SIMPLE_COACHES_QUERY } from "@/constants/graphql/coach/clientManagement";
import { getUserName } from "@/lib/username";

export default {
  name: "BulkMessengerView",
  components: {
    UserFilter,
    DateTimePicker,
    VueEditor,
    VueJsonToCsv,
  },
  data() {
    return {
      selected: [],
      bulkMessage: null,
      recipientList: [],
      coaches: [],
      scheduleDialog: false,
      scheduleMessageDateTime: null,
      toolbar: [
        [{ header: [false, 1, 2, 3, 4, 5, 6] }],
        ["bold", "italic", "underline"], // toggled buttons
        /*[
          { align: "" },
          { align: "center" },
          { align: "right" },
          { align: "justify" }
        ],*/
        // ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
        [{ color: [] }], // dropdown with defaults from theme
        // ["link", "image", "video"],
        ["clean"], // remove formatting button
      ],
      loading: {
        existingBulkMessage: false,
        creatingBulkMessage: false,
        updatingBulkMessage: false,
      },
      formData: {
        title: "",
        message: "",
        attachment: null,
        attachmentType: null,
        sender: null,
        sendNotification: false,
      },
      table: {
        headers: [
          { text: "First Name", sortable: true, value: "first_name" },
          { text: "Surname", sortable: true, value: "last_name" },
          { text: "Phone Number", sortable: true, value: "phone_number" },
          { text: "BM received (last 7 days)", value: "num_bulk_messages" },
          { text: "Product", sortable: true, value: "product" },
          { text: "Engagement Score (last 7 days)", sortable: true, value: "engagement_score" },
          { text: "Reward Balance", sortable: true, value: "reward_balance" },
          { text: "Date of last workout", sortable: true, value: "last_workout_date" },
          { text: "last workout logged", sortable: false, value: "last_workout_logged" },
          { text: "Last message text", sortable: false, value: "last_message_text" },
          { text: "Coach Name", sortable: true, value: "coach_name" },
          { text: "Last Subscription End Date", value: "last_subscription_end_date" },
        ],
      },
      options: {
        attachmentTypes: [
          { text: "Image", value: "IMAGE" },
          { text: "Video", value: "VIDEO" },
          { text: "Audio", value: "AUDIO" },
          { text: "PDF", value: "PDF" },
          { text: "GIF", value: "GIF" },
        ],
      },
    };
  },
  created() {
    if (this.$route.params.bulkMessageId) {
      this.loadBulkMessage(this.$route.params.bulkMessageId);
    }
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (!vm.$store.getters.user.isSuperuser) {
        vm.$router.push("/chats");
      }
    });
  },
  computed: {
    CSVData() {
      let data = [];
      this.recipientList.forEach((element) => {
        const obj = JSON.parse(JSON.stringify(element));
        delete obj.disabled;
        data.push(obj);
      });
      return data;
    },
  },
  methods: {
    async loadBulkMessage(bulkMessageId) {
      this.loading.existingBulkMessage = true;
      const result = await this.$apollo.query({
        query: BULK_MESSAGE_QUERY,
        variables: {
          bulkMessageId: bulkMessageId,
        },
        fetchPolicy: "no-cache",
      });
      let bulkMessage = result.data.bulkMessage;
      this.bulkMessage = bulkMessage;
      this.$refs.filter.filters = JSON.parse(bulkMessage.filters);
      this.rebuildRecipientList(bulkMessage.bulkmessagerecipientSet);
      this.formData.title = bulkMessage.title;
      this.formData.message = bulkMessage.messageText;
      this.formData.sender = bulkMessage.sender.id;
      this.formData.sendNotification = bulkMessage.sendNotification;
      this.scheduleMessageDateTime = bulkMessage.sendAt;
      this.loading.existingBulkMessage = false;
    },

    rebuildRecipientList(recipientSetMap) {
      this.recipientList = recipientSetMap.map((element) => {
        let coach = element.recipient.userProfile?.activeSubscription?.coach;
        let coachName = coach ? coach.firstName + " " + coach.lastName : "";
        let lastCoachMessageSender = element.lastCoachMessage ? element.lastCoachMessage.user.firstName : "";
        let lastCoachMessageText = element.lastCoachMessage
          ? element.lastCoachMessage?.text
              ?.split(" ")
              ?.splice(-10)
              ?.join(" ")
          : "";
        return {
          id: element.id,
          selected: element.send,
          first_name: element.recipient.firstName,
          last_name: element.recipient.lastName,
          phone_number: element.recipient.userProfile.phone,
          num_bulk_messages: element.recipient.userProfile.numBulkMessagesInLast7Days,
          product: element.recipient.userProfile?.activeSubscription?.product?.type,
          engagement_score: element.recipient.userProfile.engagementScore,
          reward_balance: element.recipient.userProfile.rewardBalance,
          last_workout_date: element.recipient.userProfile?.lastWorkout?.date,
          last_workout_logged: element.recipient.userProfile?.lastWorkout?.unitActivity?.title,
          last_message_text: lastCoachMessageSender ? `(${lastCoachMessageSender}) -> ${lastCoachMessageText}` : "",
          coach_name: coachName,
          disabled: !!this.bulkMessage.sentAt,
          last_subscription_end_date: element.recipient.userProfile.lastSubscriptionEndDate,
        };
      });
      this.selected = [];
      this.recipientList.forEach((element) => {
        if (element.selected) {
          this.selected.push(element);
        }
      });
    },
    async createBulkMessage() {
      let validationResult = await this.$refs.observer.validate();
      if (!validationResult) return;

      this.loading.creatingBulkMessage = true;
      let result = await this.$apollo.mutate({
        mutation: CREATE_BULK_MESSAGE_MUTATION,
        variables: {
          data: {
            title: this.formData.title,
            senderId: this.formData.sender,
            messageText: this.formData.message,
            attachment: this.formData.attachment,
            attachmentType: this.formData.attachmentType,
            sendNotification: this.formData.sendNotification,
            filters: this.$refs.filter.filters,
          },
        },
      });
      if (result.data.createBulkMessageMutation.success) {
        this.bulkMessage = result.data.createBulkMessageMutation.bulkMessage;
        this.rebuildRecipientList(this.bulkMessage.bulkmessagerecipientSet);
        history.pushState({}, null, this.$route.path + this.bulkMessage.id);
      }
      this.loading.creatingBulkMessage = false;
    },
    async updateBulkMessage() {
      let validationResult = await this.$refs.observer.validate();
      if (!validationResult) return;

      this.loading.updatingBulkMessage = true;
      let sendList = this.selected.map((element) => element.id);
      let result = await this.$apollo.mutate({
        mutation: UPDATE_BULK_MESSAGE_MUTATION,
        variables: {
          data: {
            title: this.formData.title,
            bulkMessageId: this.bulkMessage.id,
            senderId: this.formData.sender,
            messageText: this.formData.message,
            attachment: this.formData.attachment,
            attachmentType: this.formData.attachmentType,
            sendNotification: this.formData.sendNotification,
            filters: this.$refs.filter.filters,
            sendList: sendList,
          },
        },
      });
      if (result.data.updateBulkMessageMutation.requiresReload) {
        this.rebuildRecipientList(result.data.updateBulkMessageMutation.bulkMessage.bulkmessagerecipientSet);
      }
      this.loading.updatingBulkMessage = false;
    },
    async handleRecipientChange(data) {
      if (this.bulkMessage.sentAt) return;
      await this.$nextTick(); // wait for the DOM to update
      await this.updateBulkMessage();
    },
    scheduleMessage() {
      this.scheduleDialog = true;
    },
    async sendMessage() {
      let result = await this.$apollo.mutate({
        mutation: UPDATE_BULK_MESSAGE_MUTATION,
        variables: {
          data: {
            bulkMessageId: this.bulkMessage.id,
            scheduledAt: this.$moment(this.scheduleMessageDateTime, "YYYY-MM-DD hh:mm"),
          },
        },
      });
      if (result.data.updateBulkMessageMutation.success) {
        this.bulkMessage = result.data.updateBulkMessageMutation.bulkMessage;
        this.scheduleDialog = false;
      }
    },
    niceDate(iso_string) {
      return fancy_date_from_iso(iso_string);
    },
  },
  apollo: {
    coaches: {
      query: SIMPLE_COACHES_QUERY,
      fetchPolicy: "cache-and-network",
      variables() {
        return {
          includeCurrentCoach: true,
        };
      },
      update(data) {
        return data.simpleCoaches.map((element) => {
          return {
            value: element.id,
            text: getUserName(element),
          };
        });
      },
    },
  },
};
</script>
